/**
 * What a setting is (WALLIYO-0066).
 *
 * Walliyo has two words for configurable behaviour:
 *
 * - **Settings**: a value set at some scope, from one person up to the whole
 *   system. There are three kinds:
 *   - `user_setting`: a customer's own choices in the app (theme, lead radius).
 *   - `staff_setting`: a staff member's own choices in the console.
 *   - `feature_enablement`: what someone can see or do, switched on by staff
 *     ("issue refunds" for Finance, "new booking flow" for pros).
 * - **Rules**: one value for the whole company, versioned, often with a legal
 *   side. See `../rules/types.ts`.
 *
 * Definitions live in code: developers add settings, and staff only change
 * their values. A setting does nothing unless code checks it.
 *
 * Where a value is stored is deliberately not part of the definition.
 *
 * Names and descriptions come from the translation files, looked up by `settingId`.
 */

import type { ValueOrList, ValueTypeName } from "./value-types";

// ── Scopes: the level a value is set at ──────────────────────────────────

export type StaffScope = "allStaff" | "staffRole" | "staffMember";

export type CustomerScope =
  | "allClients"
  | "clientProfile"
  | "allPros"
  | "proProfile"
  | "user"
  | "region"
  | "appVersion";

/**
 * `system` is everyone. When several scopes hold a value for one person, the
 * most specific wins: person or profile, then role, then all clients, pros or
 * staff, then region and app version, then system.
 */
export type SettingScope = "system" | StaffScope | CustomerScope;

// ── Settings ──────────────────────────────────────────────────────────────

export type SettingKind =
  | "user_setting"
  | "staff_setting"
  | "feature_enablement"
  // Defined in ../permissions (ADR-0019): whether a staff member may take an
  // action under a feature, and the most they may do alone.
  | "permission"
  | "permission_limit";

/** Groups settings on screens, e.g. the role editor's sections. */
export type SettingCategory =
  | "appearance"
  | "notifications"
  | "leads"
  | "discovery"
  | "jobs"
  | "profile"
  | "accounts"
  | "support"
  | "onBehalf"
  | "onboarding"
  | "payments"
  | "safety"
  | "messaging"
  | "configuration"
  | "staff"
  | "audit";

/**
 * Who may switch a feature enablement on for someone. Nobody can grant
 * anything to themselves.
 * - `low`: anyone who can assign roles
 * - `high`: Ops Manager or Owner
 * - `critical`: Owner only, and the Owner is notified
 */
export type Sensitivity = "low" | "high" | "critical";

/**
 * - `planned`: defined and agreed, not built yet. Code must not rely on it.
 * - `active`: built and in use.
 * - `retired`: no longer used, kept because the audit log refers to it.
 */
export type Status = "planned" | "active" | "retired";

/**
 * A setting's permanent, readable identity:
 * <NAME>_<USER|STAFF>_<SETTING|FEATURE_ENABLEMENT>, e.g.
 * QUOTE_JOBS_USER_FEATURE_ENABLEMENT. It says what the setting is, whose it
 * is, and what sort it is.
 */
export type UserSettingId = `${string}_USER_SETTING`;
export type StaffSettingId = `${string}_STAFF_SETTING`;
export type FeatureEnablementId = `${string}_${"USER" | "STAFF"}_FEATURE_ENABLEMENT`;

export type SettingIdPattern =
  | `${string}_${"USER" | "STAFF"}_${"SETTING" | "FEATURE_ENABLEMENT"}`
  | `${string}_STAFF_PERMISSION`
  | `${string}_STAFF_PERMISSION_LIMIT`;

/** What every setting has, whatever its kind. */
export interface SettingBase<
  I extends SettingIdPattern = SettingIdPattern,
  N extends ValueTypeName = ValueTypeName,
  L extends boolean = boolean,
> {
  /**
   * Permanent, readable identity, e.g. QUOTE_JOBS_USER_FEATURE_ENABLEMENT.
   * Code, translations, stored values and the audit log all use it. Never
   * changes.
   */
  settingId: I;
  /**
   * What it is called, in English: the source text a screen shows instead of
   * the id, and what the other seven languages translate (#185). Short, and
   * it reads as a thing, not a sentence.
   */
  name: string;
  kind: SettingKind;
  category: SettingCategory;
  /** The levels a value can be set at. */
  scopes: readonly SettingScope[];
  /** The named value type, from VALUE_TYPES. */
  valueType: N;
  /** True when the setting holds several values of its type, e.g. several weekdays. */
  isList: L;
  defaultValue: ValueOrList<N, L>;
  /** Settings are never deleted: retired ones stay because the audit log refers to them. */
  status: Status;
}

/** A customer's own choice in the app. Set per person or per profile. */
export interface UserSetting<
  I extends UserSettingId = UserSettingId,
  N extends ValueTypeName = ValueTypeName,
  L extends boolean = boolean,
> extends SettingBase<I, N, L> {
  kind: "user_setting";
  scopes: readonly ("user" | "clientProfile" | "proProfile")[];
}

/** A staff member's own choice in the console. */
export interface StaffSetting<
  I extends StaffSettingId = StaffSettingId,
  N extends ValueTypeName = ValueTypeName,
  L extends boolean = boolean,
> extends SettingBase<I, N, L> {
  kind: "staff_setting";
  scopes: readonly ["staffMember"];
}

/**
 * What someone can see or do, switched on or off by staff.
 * Its scopes are either all staff scopes or all customer scopes, never mixed,
 * so a staff feature can never be switched on for a customer.
 */
export interface FeatureEnablement<I extends FeatureEnablementId = FeatureEnablementId> extends SettingBase<
  I,
  "Boolean",
  false
> {
  /** Staff features are always off until granted; customer features say. */
  defaultValue: boolean;
  kind: "feature_enablement";
  scopes: readonly StaffScope[] | readonly ("system" | CustomerScope)[];
  sensitivity: Sensitivity;
}

export type Setting = UserSetting | StaffSetting | FeatureEnablement;

// ── Define functions: write what varies, the kind fixes the rest ──────────

export function defineUserSetting<
  const I extends UserSettingId,
  const N extends ValueTypeName,
  const L extends boolean,
>(setting: Omit<UserSetting<I, N, L>, "kind" | "status"> & { status?: Status }): UserSetting<I, N, L> {
  return {
    settingId: setting.settingId,
    name: setting.name,
    kind: "user_setting",
    category: setting.category,
    scopes: setting.scopes,
    valueType: setting.valueType,
    isList: setting.isList,
    defaultValue: setting.defaultValue,
    status: setting.status ?? "active",
  };
}

export function defineStaffSetting<
  const I extends StaffSettingId,
  const N extends ValueTypeName,
  const L extends boolean,
>(
  setting: Omit<StaffSetting<I, N, L>, "kind" | "scopes" | "status"> & {
    status?: Status;
  },
): StaffSetting<I, N, L> {
  return {
    settingId: setting.settingId,
    name: setting.name,
    kind: "staff_setting",
    category: setting.category,
    scopes: ["staffMember"],
    valueType: setting.valueType,
    isList: setting.isList,
    defaultValue: setting.defaultValue,
    status: setting.status ?? "active",
  };
}

/**
 * A console feature. Always granted to a staff role or one staff member, and
 * always off until granted.
 */
export function defineStaffFeatureEnablement<const I extends `${string}_STAFF_FEATURE_ENABLEMENT`>(setting: {
  settingId: I;
  name: string;
  category: SettingCategory;
  sensitivity: Sensitivity;
  status?: Status;
}): FeatureEnablement<I> {
  return {
    settingId: setting.settingId,
    name: setting.name,
    kind: "feature_enablement",
    category: setting.category,
    scopes: ["staffRole", "staffMember"],
    valueType: "Boolean",
    isList: false,
    defaultValue: false,
    sensitivity: setting.sensitivity,
    status: setting.status ?? "active",
  };
}

/**
 * A feature of the customer app. `defaultValue` is written out every time:
 * true for a feature everyone already has, false for one switched on
 * gradually.
 */
export function defineUserFeatureEnablement<const I extends `${string}_USER_FEATURE_ENABLEMENT`>(setting: {
  settingId: I;
  name: string;
  category: SettingCategory;
  scopes: readonly ("system" | CustomerScope)[];
  defaultValue: boolean;
  sensitivity: Sensitivity;
  status?: Status;
}): FeatureEnablement<I> {
  return {
    settingId: setting.settingId,
    name: setting.name,
    kind: "feature_enablement",
    category: setting.category,
    scopes: setting.scopes,
    valueType: "Boolean",
    isList: false,
    defaultValue: setting.defaultValue,
    sensitivity: setting.sensitivity,
    status: setting.status ?? "active",
  };
}
